import { Bell, UserPlus, AlertTriangle, Calendar, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNotifications, type Notification } from "@/hooks/useNotifications";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";

const iconMap: Record<string, React.ElementType> = {
  UserPlus,
  AlertTriangle,
  Calendar,
  Bell,
};

const categoryColors: Record<string, string> = {
  member: "text-primary",
  risk: "text-destructive",
  event: "text-warning",
  general: "text-muted-foreground",
};

const NotificationBell = () => {
  const { data: notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const navigate = useNavigate();

  const handleClick = (n: Notification) => {
    if (!n.is_read) markRead.mutate(n.id);
    if (n.link_to) navigate(n.link_to);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="relative p-2 rounded-md hover:bg-secondary transition-colors">
          <Bell className="w-4 h-4 text-muted-foreground" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 max-h-[420px] flex flex-col">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between shrink-0">
          <h3 className="text-xs font-semibold text-foreground">Notifications</h3>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={() => markAllRead.mutate()}
              className="text-[10px] text-primary hover:underline flex items-center gap-1"
            >
              <CheckCheck className="w-3 h-3" /> Mark all read
            </button>
          )}
        </div>
        <div className="overflow-y-auto flex-1">
          {!notifications || notifications.length === 0 ? (
            <div className="py-8 px-4 text-center">
              <Bell className="w-5 h-5 text-muted-foreground mx-auto mb-2" />
              <p className="text-[11px] text-muted-foreground">No notifications yet</p>
            </div>
          ) : (
            notifications.map((n) => {
              const Icon = iconMap[n.icon] || Bell;
              return (
                <button
                  type="button"
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`flex items-start gap-3 w-full px-4 py-3 text-left hover:bg-secondary/60 transition-colors border-b border-border/30 last:border-b-0 ${
                    !n.is_read ? "bg-primary/5" : ""
                  }`}
                >
                  <div className={`mt-0.5 shrink-0 ${categoryColors[n.category] || categoryColors.general}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-foreground truncate">{n.title}</span>
                      {!n.is_read && <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
                    </div>
                    <p className="text-[10px] text-muted-foreground truncate mt-0.5">{n.body}</p>
                    <p className="text-[9px] text-muted-foreground/70 mt-1">
                      {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
